import { QueryClient } from '@tanstack/react-query';
import { fetchFireworkProducts } from '@/api/use-get-firework-products';
import { FireworkProduct, FireworkStore, FireworkInputProduct, SDMProduct } from '@/store/firework/firework.types';

const queryClient = new QueryClient();

// Helper method to convert SDM product to Firework product object
const createFWProduct = (sdmProduct: SDMProduct, fwProduct: FireworkInputProduct) => {
  // Firework's product_units is an array but we only support single product per video currently for hydration
  // We take first product unit [0] as per Firework's recommendation for basic integration
  const productUnit = fwProduct.product_units?.[0];

  if (!productUnit) return null;

  return window?._fwn?.shopping.productFactory((product) => {
    // 1. Update the product with SDM details
    product
      .extId(productUnit.product_ext_id)
      .name(sdmProduct.name)
      .description(sdmProduct.description)
      .currency(sdmProduct.price.currencyIso);

    // 2. Update product variant price and availability
    product.variant((variant: any) => {
      const currentPrice = Number(sdmProduct.specialPrice?.value || sdmProduct.price.value);
      const originalPrice = Number(
        fwProduct.product_units?.find((unit) => unit.unit_ext_id === productUnit.product_ext_id)?.unit_original_price
      );

      variant
        .extId(productUnit.unit_ext_id)
        .name(sdmProduct.name)
        .price(currentPrice)
        .originalPrice(originalPrice)
        .url(sdmProduct.url)
        .isAvailable(sdmProduct.isAvailable);

      variant.image((imageBuilder: any) => {
        imageBuilder.extId(productUnit.unit_ext_id).url(sdmProduct.images.thumbnail).extId(0).position(0);
      });

      return variant;
    });

    return product;
  });
};

export const initializeFireworkHydration = (store: FireworkStore): (() => void) => {
  // Store the callback reference for cleanup
  let onProductsLoadedCallback:
    | ((props: { products: FireworkInputProduct[]; video?: { hashTags?: string[] } }) => Promise<any>)
    | null = null;

  // Configure callback to hydrate products when they are loaded
  if (window._fwn?.shopping) {
    onProductsLoadedCallback = async ({ products, video = { hashTags: [] } }) => {
      try {
        // Get product IDs directly from Firework products
        // through hash tags and product units
        const productIds = [
          ...new Set(
            products
              .map((fwProduct) => {
                // Each Firework video can technically have multiple products,
                // but we only support hydration for videos with a single product so we use [0]
                const productUnit = fwProduct.product_units?.[0];
                const hashTags = video.hashTags || [];
                return [productUnit?.product_ext_id, ...hashTags];
              })
              .flat()
              .filter(Boolean)
              .filter((id) => id?.toLowerCase().startsWith('sdm_'))
          ),
        ];

        if (!productIds.length) return products;

        // Check which products need to be fetched
        const missingProductIds = productIds.filter((id) => !store.hydratedProducts[id as string]);

        // Make server request only for missing products
        let productDetails = { products: {} };

        if (missingProductIds.length) {
          productDetails = await queryClient.fetchQuery({
            queryKey: ['products', missingProductIds],
            queryFn: () => fetchFireworkProducts(missingProductIds.filter(Boolean) as string[]),
          });

          // Store the new products if they exist
          if (productDetails?.products) {
            Object.entries(productDetails.products).forEach(([id, data]) => {
              store.setHydratedProduct(id, data as FireworkProduct);
            });
          }
        }

        // Map each Firework product to hydrated version
        const hydratedProducts = products
          .map((fwProduct) => {
            const productUnit = fwProduct.product_units?.[0];
            if (!productUnit) return null;

            // Get product from store or fetched details
            const sdmProduct =
              store.hydratedProducts[productUnit.product_ext_id] ||
              (productDetails?.products as Record<string, any>)[productUnit.product_ext_id];

            if (!sdmProduct) return null;

            return createFWProduct(sdmProduct, fwProduct);
          })
          .filter(Boolean);

        return hydratedProducts;
      } catch (error) {
        console.error('Failed to hydrate products:', error);
        return products;
      }
    };

    window._fwn.shopping.onProductsLoaded(onProductsLoadedCallback);
  }

  // Return cleanup function
  return () => {
    // Remove the callback if it exists
    if (window._fwn?.shopping && onProductsLoadedCallback) {
      window._fwn.shopping.onProductsLoaded(() => {});
      onProductsLoadedCallback = null;
    }
  };
};
